import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LOGO_HERO_URL } from "@/lib/brand";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";

const SetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("No verification token in URL");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/set-password", { token, password });
      await refresh();
      setDone(true);
      toast.success("Password set! You're now signed in.");
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set password");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 pav-fade-in" data-testid="set-password-page">
      <div className="w-full max-w-md pav-card">
        <img src={LOGO_HERO_URL} alt="Print Ad Venture" className="h-16 w-auto object-contain mx-auto mb-6" />
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-3" />
            <h1 className="font-display text-2xl font-extrabold text-[#0B1E3F]">Password set!</h1>
            <p className="text-neutral-600 mt-2 text-sm">Redirecting to your dashboard…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-extrabold text-[#0B1E3F] text-center">Set your password</h1>
            <p className="text-sm text-neutral-600 text-center mt-2">
              Welcome to Print Ad Venture. Choose a password to finish creating your account.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" /> New password
                </label>
                <input type="password" className="pav-input mt-1.5" data-testid="set-password-input"
                  placeholder="At least 6 characters" autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" /> Confirm password
                </label>
                <input type="password" className="pav-input mt-1.5" data-testid="set-password-confirm-input"
                  placeholder="Re-enter password" autoComplete="new-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              <button type="submit" disabled={submitting || !token} data-testid="set-password-submit"
                className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Set password &amp; sign in
              </button>
            </form>
            <p className="text-xs text-neutral-500 text-center mt-5">
              Wrong account? <Link to="/login" className="text-[#0B1E3F] font-semibold hover:underline">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SetPasswordPage;
